import { useEffect, useState } from 'react';

import {
  GetTiloWorldRecordsAsync,
  TiloWorldRecord
} from '../../services/tilo/tilo-proxy';
import { ctGameDefault, ctGameTitle } from '../../constants/text';
import medalGold from '../../assets/tilo/medal-gold.png';
import medalSilver from '../../assets/tilo/medal-silver.png';
import medalBronze from '../../assets/tilo/medal-bronze.png';

type Doors = 8 | 12;

const medals = [medalGold, medalSilver, medalBronze];

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
};

const formatDate = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export default function TiloWorldRecords() {
  const [doors, setDoors] = useState<Doors>(8);
  const [records, setRecords] = useState<TiloWorldRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `${ctGameTitle} - World Records`;
    return () => {
      document.title = ctGameDefault;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    GetTiloWorldRecordsAsync(doors)
      .then((data) => {
        if (cancelled) return;
        const sorted = [...data].sort((a, b) => a.timeWin - b.timeWin);
        setRecords(sorted);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load world records.');
        setRecords([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [doors]);

  const podium = [0, 1, 2].map((i) => records[i]);
  const rest = records.slice(3);

  return (
    <div className={'tilo-world-records'}>
      <div className={'tilo-world-records__header'}>
        <h1 className={'tilo-world-records__title'}>
          The Infinite Loop Online
        </h1>
        <div className={'tilo-world-records__doors'}>
          {([8, 12] as Doors[]).map((value) => (
            <button
              key={value}
              type={'button'}
              className={`tilo-world-records__doors-btn${
                doors === value ? ' is-active' : ''
              }`}
              onClick={() => setDoors(value)}>
              {value} Doors
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className={'tilo-world-records__status'}>{error}</div>
      )}

      <div className={'tilo-world-records__podium'}>
        {podium.map((record, index) => (
          <div
            key={index}
            className={`tilo-world-records__card${
              record ? '' : ' is-empty'
            }`}>
            <img
              src={medals[index]}
              alt={`Rank ${index + 1} medal`}
              className={'tilo-world-records__medal'}
            />
            <div className={'tilo-world-records__card-header'}>
              <span className={'tilo-world-records__team'}>
                {record?.name || '—'}
              </span>
              <span className={'tilo-world-records__time'}>
                {formatTime(record?.timeWin ?? 0)}
              </span>
            </div>
            <div className={'tilo-world-records__card-rows'}>
              <div className={'tilo-world-records__card-row'}>
                <span className={'tilo-world-records__card-label'}>Game</span>
                <span className={'tilo-world-records__card-value'}>
                  {record?.game?.name || '—'}
                </span>
              </div>
              <div className={'tilo-world-records__card-row'}>
                <span className={'tilo-world-records__card-label'}>
                  Company
                </span>
                <span className={'tilo-world-records__card-value'}>
                  {record?.game?.company || '—'}
                </span>
              </div>
              <div className={'tilo-world-records__card-row'}>
                <span className={'tilo-world-records__card-label'}>
                  Client
                </span>
                <span className={'tilo-world-records__card-value'}>
                  {record?.game?.client || '—'}
                </span>
              </div>
              <div className={'tilo-world-records__card-row'}>
                <span className={'tilo-world-records__card-label'}>
                  Country
                </span>
                <span className={'tilo-world-records__card-value'}>
                  {record?.game?.country || '—'}
                </span>
              </div>
              <div className={'tilo-world-records__card-row'}>
                <span className={'tilo-world-records__card-label'}>Date</span>
                <span className={'tilo-world-records__card-value'}>
                  {formatDate(record?.game?.startedAt || '')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={'tilo-world-records__table-wrap'}>
        <table className={'tilo-world-records__table'}>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Game</th>
              <th>Name</th>
              <th>Timewin</th>
              <th>Client</th>
              <th>Company</th>
              <th>Country</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {rest.map((record, index) => (
              <tr key={`${record.game?.startedAt ?? ''}-${record.name}-${index}`}>
                <td>{index + 4}</td>
                <td>{record.game?.name || '—'}</td>
                <td>{record.name || '—'}</td>
                <td>{formatTime(record.timeWin)}</td>
                <td>{record.game?.client || '—'}</td>
                <td>{record.game?.company || '—'}</td>
                <td>{record.game?.country || '—'}</td>
                <td>{formatDate(record.game?.startedAt || '')}</td>
              </tr>
            ))}
            {!loading && rest.length === 0 && (
              <tr>
                <td colSpan={8} className={'tilo-world-records__status'}>
                  No additional records.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={8} className={'tilo-world-records__status'}>
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
